/**
 * <s-checkout-guard> — Requires a "double-checked my sizes" confirmation before checkout.
 *
 * Wraps a checkbox inside a cart form. Until it's checked, the form's Checkout
 * button is disabled and any accelerated checkout buttons are made inert.
 * The confirmation is remembered for this browser session against the cart's
 * contents (data-cart-signature), so the drawer and cart page agree and partial
 * refreshes don't clear it, but adding or changing an item asks again.
 */
const STORAGE_KEY = 'ldd-size-confirmed';

class CheckoutGuard extends HTMLElement {
  #ac;

  connectedCallback() {
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };
    const form = this.closest('form');
    const checkbox = this.querySelector('input[type="checkbox"]');
    if (!form || !checkbox) return;

    const signature = this.dataset.cartSignature || '';
    checkbox.checked = this.#readConfirmed() === signature;

    const sync = () => {
      const confirmed = checkbox.checked;
      form.querySelectorAll('[name="checkout"]').forEach((button) => {
        button.disabled = !confirmed;
        button.setAttribute('aria-disabled', String(!confirmed));
      });
      const accelerated = form.querySelector('.additional-checkout-buttons');
      if (accelerated) {
        accelerated.inert = !confirmed;
        accelerated.classList.toggle('opacity-50', !confirmed);
      }
    };

    checkbox.addEventListener('change', () => {
      this.#writeConfirmed(checkbox.checked ? signature : '');
      sync();
    }, opts);

    // Safety net: never start checkout without the confirmation.
    form.addEventListener('submit', (event) => {
      if (checkbox.checked) return;
      if (event.submitter?.getAttribute('name') !== 'checkout') return;
      event.preventDefault();
      event.stopPropagation();
      checkbox.focus();
    }, { ...opts, capture: true });

    sync();
  }

  disconnectedCallback() {
    this.#ac?.abort();
  }

  #readConfirmed() {
    try { return sessionStorage.getItem(STORAGE_KEY); } catch { return null; }
  }

  #writeConfirmed(value) {
    try { sessionStorage.setItem(STORAGE_KEY, value); } catch { /* storage unavailable */ }
  }
}

if (!customElements.get('s-checkout-guard')) customElements.define('s-checkout-guard', CheckoutGuard);
